import { PlusIcon } from '@shopify/polaris-icons';
/**
 * AUTOMATION RULES PAGE
 *
 * List and manage automation rules that trigger actions on events.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Banner,
  EmptyState,
  Spinner,
  Modal,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import AutomationPreview from '../components/AutomationPreview';
import AutomationExecution from '../components/AutomationExecution';

interface AutomationRule {
  id: number;
  name: string;
  trigger_event: string;
  conditions: Array<{ field: string; operator: string; value: string }>;
  actions: Array<{ type: string; value: string }>;
  priority: number;
  enabled: boolean;
  execution_enabled: number | null;
  execution_revision: number | null;
  execution_problem: string | null;
  run_count: number;
  last_run_at: string | null;
  created_at: string;
}

interface RulesResponse {
  data: AutomationRule[];
}

export default function AutomationRules() {
  const navigate = useNavigate();
  const { fetchApi, loading, error } = useApi();
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<AutomationRule | null>(null);
  const [executionRule, setExecutionRule] = useState<number | null>(null);
  const [previewRule, setPreviewRule] = useState<AutomationRule | null>(null);

  const loadRules = useCallback(async () => {
    const data = await fetchApi<RulesResponse>('/automation/rules');
    if (data?.data) {
      setRules(data.data.map(rule => ({ ...rule, enabled: Boolean(rule.enabled) })));
    }
  }, [fetchApi]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleToggleEnabled = async (rule: AutomationRule) => {
    await fetchApi(`/automation/rules/${rule.id}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled: !rule.enabled }),
    });
    loadRules();
  };

  const handleDelete = async () => {
    if (!ruleToDelete) return;

    const result = await fetchApi(`/automation/rules/${ruleToDelete.id}`, {
      method: 'DELETE',
    });

    if (result) {
      setDeleteConfirmOpen(false);
      setRuleToDelete(null);
      loadRules();
    }
  };

  const getTriggerLabel = (trigger: string) => {
    const labels: Record<string, string> = {
      order_created: 'Order Created',
      order_allocated: 'Order Allocated',
      order_picked: 'Order Picked',
      order_packed: 'Order Packed',
      order_dispatched: 'Order Dispatched',
      stock_low: 'Stock Low',
      return_requested: 'Return Requested',
    };
    return labels[trigger] || trigger;
  };

  const getActionSummary = (actions: Array<{ type: string; value: string }>) => {
    return actions.map(a => {
      const labels: Record<string, string> = {
        set_tag: 'Add tag',
        remove_tag: 'Remove tag',
        add_note: 'Add note',
        set_status: 'Set status',
        set_priority: 'Set priority',
        notify: 'Send notification',
        webhook: 'Call webhook',
      };
      return labels[a.type] || a.type;
    }).join(', ');
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z').toLocaleString();
  };

  if (loading && rules.length === 0) {
    return (
      <Page title="Automation Rules">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading automation rules...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Automation Rules"
      subtitle="Build, preview and activate rules for your orders"
      primaryAction={{
        icon: PlusIcon, content: 'Create Rule',
        onAction: () => navigate('/automation/new'),
      }}
      secondaryActions={[
        {
          content: 'View Logs',
          onAction: () => navigate('/automation/logs'),
        },
      ]}
    >
      <Layout>
        <Layout.Section><Banner tone="info">Order rules can add or remove tags, append notes and change priority automatically. Review and activate execution for each rule. Enabling a saved definition alone does not start it.</Banner></Layout.Section>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {rules.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text']}
                  headings={['Name', 'Trigger', 'Actions', 'Run Count', 'Status', 'Actions']}
                  rows={rules.map((rule) => [
                    <BlockStack gap="100" key={`name-${rule.id}`}>
                      <Text as="span" fontWeight="semibold">{rule.name}</Text>
                      <Text as="span" tone="subdued" variant="bodySm">
                        Last run: {formatDate(rule.last_run_at)}
                      </Text>
                    </BlockStack>,
                    <Badge key={`trigger-${rule.id}`}>{getTriggerLabel(rule.trigger_event)}</Badge>,
                    getActionSummary(rule.actions),
                    rule.run_count,
                    <Badge
                      key={`status-${rule.id}`}
                      tone={rule.execution_enabled ? 'success' : undefined}
                    >
                      {rule.execution_enabled ? 'Running' : rule.enabled ? 'Saved, not running' : 'Disabled'}
                    </Badge>,
                    <InlineStack gap="200" key={`actions-${rule.id}`}>
                      <Button
                        size="slim"
                        onClick={() => navigate(`/automation/${rule.id}`)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="slim"
                        onClick={() => setPreviewRule(rule)}
                      >
                        Preview
                      </Button>
                      <Button size="slim" onClick={() => setExecutionRule(rule.id)}>Review execution</Button>
                      <Button
                        size="slim"
                        onClick={() => handleToggleEnabled(rule)}
                      >
                        {rule.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        size="slim"
                        tone="critical"
                        onClick={() => {
                          setRuleToDelete(rule);
                          setDeleteConfirmOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </InlineStack>,
                  ])}
                />
              ) : (
                <EmptyState
                  heading="No automation rules"
                  action={{
                    icon: PlusIcon, content: 'Create Rule',
                    onAction: () => navigate('/automation/new'),
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Save rules and preview them with sample values.
                    Review and activate each supported order rule to run it automatically.
                  </p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      {previewRule && <AutomationPreview key={previewRule.id} rule={previewRule} close={() => setPreviewRule(null)} />}

      {executionRule && <AutomationExecution key={executionRule} ruleId={executionRule} close={() => setExecutionRule(null)} saved={() => void loadRules()} />}

      {/* Delete Confirmation */}
      <Modal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete Automation Rule"
        primaryAction={{
          content: 'Delete',
          onAction: handleDelete,
          destructive: true,
          loading,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setDeleteConfirmOpen(false) },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            Are you sure you want to delete the rule "{ruleToDelete?.name}"?
            This action cannot be undone.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
